// THE MAP — one source for the rail, every section column, Home and the routes.
//
// Nucleus is drawn as three layers, from you outward, and every section stands
// in one:
//
//   internal — you: who you are (Self), what you hold (Graph), what happened (Activity)
//   gates    — what may pass: Realms & Contracts, the realms you set and each contract shared from them
//   external — who you let in: apps, plugins, people, groups
//
// The layer is the design language: its mark (● ◎ ○·) and its hue ride the
// rail, the section column, the location line, the inspector and Home
// (components/nucleus/zones.js, styles/Layout.css). A section is a rail door;
// its pages are the tiles of its column. A page that only exists in one space
// says so (`space`); one only a group's admins reach in a group says that
// (`admins`). A page whose line speaks as "you" carries the group's own line
// (`aboutGroup`), read through aboutOf; one whose name does too carries
// `labelGroup`, which pagesFor puts in its place. Nothing else in the app spells a
// section's name, order, zone or address.

// Each zone and stage speaks as "you"; in a group it has the group's own words
// (`lineGroup`, `titleGroup`), read through zoneOfSpace / stageOfSpace.
export const ZONES = [
    { id: 'internal', label: 'Internal', line: 'You: who you are, what you hold, and what happened.', lineGroup: 'This group: who it is, what it holds, and what happened.' },
    { id: 'gates', label: 'Gates', line: 'What may pass: your realms, and the contracts shared from them.', lineGroup: 'What may pass: this group’s realms, and the contracts shared from them.' },
    { id: 'external', label: 'External', line: 'Who you let in: apps, plugins, people and groups.', lineGroup: 'Who this group lets in: apps, plugins and its members.' },
];

export const STAGES = [
    { n: '01', id: 'you', zone: 'internal', title: 'You', line: 'Who you are here: your profile, identities and how you sign in.',
        titleGroup: 'This group', lineGroup: 'Who this group is: its identities, settings and what you share into it.', sections: ['self'] },
    { n: '02', id: 'graph', zone: 'internal', title: 'Your graph', line: 'Everything you hold, the kinds it comes in, and what is waiting to enter it.',
        titleGroup: 'This group’s graph', lineGroup: 'Everything this group holds, the kinds it comes in, and what is waiting to enter it.', sections: ['graph'] },
    { n: '03', id: 'activity', zone: 'internal', title: 'What happened', line: 'One ledger of every read, write and decision, and your inbox.',
        lineGroup: 'One ledger of everything anyone did in this group, and your inbox here.', sections: ['activity'] },
    { n: '04', id: 'realms', zone: 'gates', title: 'Realms and contracts', line: 'The parts of your identity you can share, and everyone you have shared them with.',
        lineGroup: 'The parts of this group’s graph it can share, and everyone it has shared them with.', sections: ['realms'] },
    { n: '05', id: 'entities', zone: 'external', title: 'Who you let in', line: 'The apps, plugins, people and groups you are connected to.',
        titleGroup: 'Who this group lets in', lineGroup: 'The apps, plugins and members this group is connected to.', sections: ['apps', 'plugins', 'people', 'groups'] },
];

export const SECTIONS = [
    {
        // Settings wears grey: it is where you set things, not a part of you
        id: 'self', zone: 'internal', tone: 'quiet', label: 'Settings', glyph: 'self',
        pages: [
            { id: 'profile', label: 'Profile', about: 'Who you are here: your identities, and how each one shows.', aboutGroup: 'This group’s identities, and how each one shows.' },
            { id: 'notifications', label: 'Notifications', about: 'What reaches you, and how it gets to you.', space: 'personal' },
            { id: 'security', label: 'Security', about: 'Where you are signed in, and how you get back in.', space: 'personal' },
            { id: 'data', label: 'Data', about: 'Take your data out, bring it in, and see what is kept.', aboutGroup: 'This group’s nodes, in bulk.' },
            { id: 'settings', label: 'This group', about: 'Name, photo, encryption, and leaving or deleting it.', space: 'group' },
            { id: 'share', label: 'Your share', about: 'Your two contracts with this group: what you share, and what you can do.', space: 'group' },
            { id: 'public', label: 'Public page', about: 'What anyone on ofSelf can see of this group.', space: 'group' },
            // per device, so it is the same page in every space
            { id: 'appearance', label: 'Appearance', about: 'How every ofSelf app looks on this device.' },
        ],
    },
    {
        id: 'graph', zone: 'internal', label: 'Identity graph', glyph: 'graph',
        pages: [
            { id: 'nodes', label: 'Nodes', about: 'Everything you hold, as nodes: filter it, open one, change it.', aboutGroup: 'Everything this group holds, as nodes: filter it, open one, change it.' },
            { id: 'review', label: 'Review', about: 'What apps and people want to add or change, waiting on you.', aboutGroup: 'What apps and people want to add or change in this group.' },
            { id: 'schemas', label: 'Schemas', about: 'The kinds your nodes come in, and the fields each one has.', aboutGroup: 'The kinds this group’s nodes come in, and the fields each one has.' },
            { id: 'trash', label: 'Trash', about: 'Deleted nodes, restorable until they are deleted for good.' },
        ],
    },
    {
        id: 'activity', zone: 'internal', label: 'Activity', glyph: 'activity',
        pages: [
            { id: 'log', label: 'Log', about: 'Every read, write and decision, as it happened.', aboutGroup: 'Everything anyone did in this group, and who did it.' },
            { id: 'inbox', label: 'Inbox', about: 'Notifications, and what needs you.' },
        ],
    },
    {
        // PRIVACY & ACCESS — one gate layer: the exposure profiles (each
        // connection's grant: who, through which realms, until when), then the
        // privacy realms they reach through. Kinds and ended ones are filters.
        id: 'realms', zone: 'gates', label: 'Realms & Contracts', glyph: 'realms',
        pages: [
            { id: 'all', label: 'Realms', about: 'Parts of your identity you can share: which nodes, what may be done with them, and how you appear.', aboutGroup: 'Parts of this group’s graph: which nodes, what may be done with them, and how it appears.' },
            { id: 'profiles', label: 'Contracts', about: 'Every app, person and group you have shared with, and what each can see and do.', aboutGroup: 'Every app, member and person this group shares with, and what each can see and do.' },
        ],
    },
    {
        id: 'apps', zone: 'external', label: 'Apps', glyph: 'apps',
        pages: [
            { id: 'connected', label: 'My apps', labelGroup: 'Group apps', about: 'The apps you let in, and the ones whose contracts expired.', aboutGroup: 'The apps this group let in, and the ones whose contracts expired.' },
            { id: 'store', label: 'App store', about: 'Every app you could connect.', aboutGroup: 'Every app this group could connect.' },
            { id: 'agents', label: 'Sub-entities & agents', about: 'What your apps run in your name: agents, and any other sub-entity. Each has its own contract.', aboutGroup: 'What this group’s apps run in its name: agents, and any other sub-entity. Each has its own contract.' },
            { id: 'billing', label: 'Billing', about: 'What you pay for, and your receipts.', aboutGroup: 'What this group pays for, and its receipts.', admins: true },
        ],
    },
    {
        id: 'plugins', zone: 'external', label: 'Plugins', glyph: 'plugins',
        pages: [
            { id: 'installed', label: 'My plugins', labelGroup: 'Group plugins', about: 'The plugins you set up, and their tools.', aboutGroup: 'The plugins this group set up, and their tools.' },
            { id: 'store', label: 'Plugin store', about: 'Plugins you can set up, and what each asks for.', aboutGroup: 'Plugins this group can set up, and what each asks for.' },
            { id: 'automations', label: 'Automations', about: 'What runs your plugins’ tools for you: automatically, when something changes, and in flows.', aboutGroup: 'What runs this group’s plugins’ tools for it: automatically, when something changes, and in flows.' },
            { id: 'runs', label: 'Runs', about: 'Every run of a plugin’s tool: who started it, how it went, and why.' },
        ],
    },
    {
        id: 'people', zone: 'external', label: 'People', glyph: 'people',
        pages: [
            { id: 'friends', label: 'Friends', about: 'Who you share with, both ways. Requests and past friends too.', space: 'personal' },
            { id: 'following', label: 'Following', about: 'Who you follow, who follows you, and who wants to.', space: 'personal' },
            { id: 'find', label: 'Find people', about: 'Find anyone on ofSelf, or someone you may know.', space: 'personal' },
            { id: 'public', label: 'Public page', about: 'Your public page, and who may follow you.', space: 'personal' },
            { id: 'members', label: 'Members', about: 'Who is in this group, and what each one can do.', space: 'group' },
            { id: 'invitations', label: 'Invitations', about: 'People invited to this group, yet to answer.', space: 'group' },
        ],
    },
    {
        id: 'groups', zone: 'external', label: 'Groups', glyph: 'groups', space: 'personal',
        pages: [
            { id: 'yours', label: 'Your groups', about: 'The groups you belong to.' },
            { id: 'invitations', label: 'Invitations', about: 'Groups that asked you in.' },
        ],
    },
];

// In a group, an `admins` page is listed for its admins only. Not knowing the
// viewer's role lists it for nobody; an address still resolves to it (placeOf),
// and the page itself turns a member away.
const inSpace = (thing, groupId, admin = !groupId) => (!thing.space || thing.space === (groupId ? 'group' : 'personal'))
    && (!groupId || !thing.admins || !!admin);

export const sectionsFor = (groupId, { admin } = {}) => SECTIONS.filter((s) => inSpace(s, groupId, admin));
export const pagesFor = (section, groupId, { admin } = {}) => (section?.pages || [])
    .filter((p) => inSpace(p, groupId, admin))
    .map((p) => (groupId && p.labelGroup ? { ...p, label: p.labelGroup } : p));
// A page's line in the space you are in: a group's own where it has one.
export const aboutOf = (page, groupId) => (groupId && page?.aboutGroup) || page?.about || '';
// A zone's and a stage's words in the space you are in (a stage's sections are
// the space's own: a group has no Groups).
export const zoneOfSpace = (zone, groupId) => ({ ...zone, line: (groupId && zone?.lineGroup) || zone?.line || '' });
export const stageOfSpace = (stage, groupId) => ({
    ...stage,
    title: (groupId && stage?.titleGroup) || stage?.title || '',
    line: (groupId && stage?.lineGroup) || stage?.line || '',
    sections: (stage?.sections || []).filter((id) => { const s = sectionById(id); return !!s && inSpace(s, groupId); }),
});
export const sectionById = (id) => SECTIONS.find((s) => s.id === id) || null;

// The address of a page: /<section>/<page>, or /group/<id>/<section>/<page>.
// No page names the section's first page in its space.
export function pathFor(sectionId, pageId, groupId) {
    const section = sectionById(sectionId);
    const first = pagesFor(section, groupId, { admin: true })[0]?.id;
    const page = pageId && pageId !== first ? `/${pageId}` : '';
    return `${groupId ? `/group/${groupId}` : ''}/${sectionId}${page}`;
}

// Where an address stands: { section, page } (page resolved to the space's first
// when the address names none or one this space does not have).
export function placeOf(pathname, groupId) {
    const parts = pathname.split('/').filter(Boolean);
    const rest = parts[0] === 'group' ? parts.slice(2) : parts;
    const section = sectionById(rest[0]);
    if (!section || !inSpace(section, groupId)) return { section: null, page: null };
    const pages = pagesFor(section, groupId, { admin: true });
    const page = pages.find((p) => p.id === rest[1]) || pages[0];
    return { section, page };
}

// The address of the page an address actually shows, when it names another: a
// page the section does not have, or one this space does not (/group/<id>/self/
// security shows Profile). null when the address already says what it shows.
// The space is the address's own, never a space a moment stale.
export function shownAddress(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    const groupId = parts[0] === 'group' ? parts[1] || null : null;
    const named = (groupId ? parts.slice(2) : parts)[1];
    const { section, page } = placeOf(pathname, groupId);
    if (!section || !page || !named || named === page.id) return null;
    return pathFor(section.id, page.id, groupId);
}
